import Link from 'next/link'

import clsx from 'clsx'

import type { ComponentProps, JSX } from 'react'

/** The search backend serves at most 100 pages (CAN-257). */
const MAX_PAGES = 100

const control =
  'inline-flex min-h-11 items-center rounded-lg border border-border-strong px-4 text-base font-medium text-ink no-underline'
const focusRing = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

type PaginationProps = Omit<ComponentProps<'nav'>, 'children'> & {
  currentPage: number
  totalPages: number
  /** The current search string without `page`, e.g. `q=nurse&l=austin`. */
  query?: string
}

/**
 * First / Previous / "Page N of M" / Next as plain `?page=N` links, so paging works without
 * JavaScript (S20-994): a server component, no hooks. Page 1 drops `page`; the bare form is `?`,
 * since `.` would resolve to the parent path. Unavailable controls are `aria-disabled` spans, not links.
 */
export default function Pagination({
  currentPage,
  totalPages,
  query = '',
  className,
  ...rest
}: PaginationProps): JSX.Element | null {
  const total = Math.min(Math.trunc(totalPages), MAX_PAGES)

  if (!(total > 1)) return null

  // A NaN or fractional page from a hand-edited URL must not reach the links.
  const page = Number.isFinite(currentPage) ? Math.min(Math.max(Math.trunc(currentPage), 1), total) : 1
  const hrefTo = (n: number): string => {
    const params = new URLSearchParams(query)

    params.delete('page')
    if (n > 1) params.set('page', String(n))

    return `?${params}`
  }
  const item = (label: string, target: number, enabled: boolean): JSX.Element =>
    enabled ? (
      <Link href={hrefTo(target)} prefetch={false} className={`${control} ${focusRing}`}>
        {label}
      </Link>
    ) : (
      <span aria-disabled="true" className={`${control} cursor-not-allowed text-ink-muted opacity-60`}>
        {label}
      </span>
    )

  return (
    <nav
      aria-label="Pagination"
      {...rest}
      className={clsx('flex flex-wrap items-center justify-center gap-3 font-sans', className)}
    >
      {item('First', 1, page > 1)}
      {item('Previous', page - 1, page > 1)}
      <span aria-current="page" className="px-2 text-base text-ink">
        {`Page ${page} of ${total}`}
      </span>
      {item('Next', page + 1, page < total)}
    </nav>
  )
}
